import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import FloatingAIChat from '../../components/ui/FloatingAIChat';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import GridView from './components/GridView';
import BoardsView from './components/BoardsView';
import TimelineView from './components/TimelineView';
import GoalModal from './components/GoalModal';
import { useGoals } from '../../hooks/useGoals';
import { mapStatusToLocalStorage } from '../../types/goals';

const GoalsMapInterface = () => {
  const [activeView, setActiveView] = useState('grade');
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [viewingGoal, setViewingGoal] = useState(null);
  const [initialStep, setInitialStep] = useState(1);
  const [language, setLanguage] = useState('pt');
  const [dashboardExpanded, setDashboardExpanded] = useState(true);
  
  // Use the goals hook instead of local state
  const {
    goals,
    stats,
    loading,
    creating,
    updating,
    deleting,
    error,
    createGoal: createGoalAction,
    updateGoal: updateGoalAction,
    deleteGoal: deleteGoalAction,
    updateGoalStatus: updateGoalStatusAction,
    hasLocalData,
    migrationCompleted,
    migrateLocalStorage
  } = useGoals();

  // Handle migration from localStorage on component mount
  useEffect(() => {
    if (hasLocalData && !migrationCompleted) {
      // Show migration prompt or auto-migrate
      migrateLocalStorage();
    }
  }, [hasLocalData, migrationCompleted, migrateLocalStorage]);

  const handleCreateGoal = async (goalData) => {
    const success = await createGoalAction(goalData);
    if (success) {
      setShowGoalModal(false);
    }
  };

  const handleEditGoal = async (goalData) => {
    const success = await updateGoalAction(editingGoal?.id, goalData);
    if (success) {
      setEditingGoal(null);
      setShowGoalModal(false);
    }
  };

  const handleDeleteGoal = async (goalId) => {
    await deleteGoalAction(goalId);
  };

  const handleStatusChange = async (goalId, newStatus) => {
    // Map UI status to Supabase status
    const supabaseStatus = newStatus === 'planejado' ? 'planned' :
                          newStatus === 'em_progresso' ? 'in_progress' :
                          newStatus === 'concluido' ? 'completed' :
                          newStatus === 'com_mentoria' ? 'with_mentor' :
                          newStatus === 'acompanhamento' ? 'follow_up' :
                          newStatus === 'arquivado' ? 'archived' : 'planned';
    
    await updateGoalStatusAction(goalId, supabaseStatus);
  };

  const handleViewGoal = (goal) => {
    setViewingGoal(goal);
    setShowGoalModal(true);
  };

  const handleEditClick = (goal, field = null) => {
    setEditingGoal(goal);
    
    // Mapear coluna para passo se especificado
    if (field) {
      const columnToStepMap = {
        'titulo': 1,           // Passo 1: Título da Meta
        'responsavel': 2,      // Passo 2: Responsável
        'areaVida': 3,         // Passo 3: Área da Vida
        'estadoAtual': 4,      // Passo 4: Estado Atual
        'metaFutura': 5,       // Passo 5: Alcance (Futuro)
        'medidas': 6,          // Passo 6: Medidas
        'orcamento': 7,        // Passo 7: Orçamento
        'quemPodeAjudar': 8,   // Passo 8: Quem Pode Ajudar
        'comoSeraFeito': 9,    // Passo 9: Como Será Feito
        'dataInicio': 10,      // Passo 10: Data de Início
        'dataAlvo': 11         // Passo 11: Data Alvo
      };
      setInitialStep(columnToStepMap[field] || 1);
    } else {
      setInitialStep(1);
    }
    
    setShowGoalModal(true);
  };

  const handleCloseModal = () => {
    setShowGoalModal(false);
    setEditingGoal(null);
    setViewingGoal(null);
  };

  const handleExportCSV = () => {
    if (goals?.length === 0) {
      alert('Nenhuma meta para exportar');
      return;
    }
    
    const headers = ['Status', 'Meta', 'Responsável', 'Área da Vida', 'Estado Atual', 'Meta Futura', 'Medidas', 'Orçamento', 'Quem Pode Ajudar', 'Como Será Feito', 'Data de Início', 'Data Alvo'];
    const csvContent = [
      headers?.join(','),
      ...goals?.map(goal => [
        goal?.status || '',
        `"${goal?.titulo || ''}"`,
        goal?.responsavel || '',
        goal?.areaVida || '',
        goal?.estadoAtual || '',
        goal?.metaFutura || '',
        `"${goal?.medidas || ''}"`,
        goal?.orcamento || '',
        goal?.quemPodeAjudar || '',
        `"${goal?.comoSeraFeito || ''}"`,
        goal?.dataInicio || '',
        goal?.dataAlvo || ''
      ]?.join(','))
    ]?.join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link?.setAttribute('href', url);
    link?.setAttribute('download', 'metas.csv');
    link.style.visibility = 'hidden';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  };

  const getLabels = () => {
    const labels = {
      pt: {
        pageTitle: 'Mapa de Metas',
        pageDescription: 'Gerencie suas metas e objetivos através de diferentes visualizações',
        newGoal: 'Nova Meta',
        exportCSV: 'Exportar CSV',
        language: 'Português',
        grade: 'Grade',
        quadros: 'Quadros',
        timeline: 'Linha do Tempo',
        totalGoals: 'Total de Metas',
        inProgress: 'Em Progresso',
        sectionCompleted: 'Concluídas',
        withMentor: 'Com Mentor',
        planned: 'Planejadas',
        followUp: 'Acompanhamento'
      },
      en: {
        pageTitle: 'Goals Map',
        pageDescription: 'Manage your goals and objectives through different views',
        newGoal: 'New Goal',
        exportCSV: 'Export CSV',
        language: 'English',
        grade: 'Grid',
        quadros: 'Boards',
        timeline: 'Timeline',
        totalGoals: 'Total Goals',
        inProgress: 'In Progress',
        sectionCompleted: 'Completed',
        withMentor: 'With Mentor',
        planned: 'Planned',
        followUp: 'Follow-up'
      }
    };
    return labels?.[language] || labels?.pt;
  };

  const labels = getLabels();

  const languageOptions = [
    { value: 'pt', label: 'Português' },
    { value: 'en', label: 'English' }
  ];

  const viewButtons = [
    { id: 'grade', label: labels?.grade, icon: 'Grid' },
    { id: 'quadros', label: labels?.quadros, icon: 'Kanban' },
    { id: 'timeline', label: labels?.timeline, icon: 'Clock' }
  ];

  const breadcrumbItems = [
    { label: language === 'en' ? 'Home' : 'Início', path: '/dashboard-home' },
    { label: labels?.pageTitle, path: '/goals-map-interface', isActive: true }
  ];

  // Calculate metrics from stats or fallback to goals array
  const totalGoals = stats?.total_goals || goals?.length || 0;
  const inProgressGoals = stats?.in_progress_goals || goals?.filter(g => g?.status === 'em_progresso')?.length || 0;
  const completedGoals = stats?.completed_goals || goals?.filter(g => g?.status === 'concluido')?.length || 0;
  const withMentorGoals = stats?.with_mentor_goals || goals?.filter(g => g?.status === 'com_mentoria')?.length || 0;
  const plannedGoals = stats?.planned_goals || goals?.filter(g => g?.status === 'planejado')?.length || 0;
  const followUpGoals = stats?.follow_up_goals || goals?.filter(g => g?.status === 'acompanhamento')?.length || 0;
  const archivedGoals = stats?.archived_goals || goals?.filter(g => g?.status === 'arquivado')?.length || 0;

  const renderActiveView = () => {
    switch (activeView) {
      case 'grade':
        return (
          <GridView 
            goals={goals} 
            onView={handleViewGoal}
            onEdit={handleEditClick}
            onDelete={handleDeleteGoal}
            onStatusChange={handleStatusChange}
            language={language}
          />
        );
      case 'quadros':
        return (
          <BoardsView 
            goals={goals} 
            onStatusChange={handleStatusChange}
            onView={handleViewGoal}
            onEdit={handleEditClick}
            onDelete={handleDeleteGoal}
            language={language}
          />
        );
      case 'timeline':
        return (
          <TimelineView 
            goals={goals}
            onView={handleViewGoal}
            onEdit={handleEditClick}
            onDelete={handleDeleteGoal}
            language={language}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 py-8">
          <Breadcrumb customItems={breadcrumbItems} />
          
          {/* Mobile-Responsive Page Header */}
          <div className="flex flex-col space-y-4 mb-4 sm:mb-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground mb-2">
                {labels?.pageTitle}
              </h1>
              <p className="text-muted-foreground font-caption text-sm sm:text-base">
                {labels?.pageDescription}
              </p>
            </div>
            
            {/* Mobile-Optimized Header Controls */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <div className="flex items-center space-x-2 sm:space-x-3 flex-1">
                <Button 
                  variant="outline"
                  iconName={dashboardExpanded ? "ChevronUp" : "ChevronDown"}
                  onClick={() => setDashboardExpanded(!dashboardExpanded)}
                  className="sm:hidden h-9 w-9 p-0"
                />
                
                <Button 
                  variant="default" 
                  iconName="Plus" 
                  iconPosition="left" 
                  iconSize={16}
                  onClick={() => setShowGoalModal(true)}
                  disabled={creating}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white flex-1 sm:flex-none text-sm sm:text-base h-9 sm:h-10"
                >
                  {creating ? 'Criando...' : labels?.newGoal}
                </Button>
                
                <Button
                  variant="outline"
                  iconName="Download"
                  iconPosition="left"
                  iconSize={16}
                  onClick={handleExportCSV}
                  className="flex-1 sm:flex-none text-sm sm:text-base h-9 sm:h-10"
                >
                  <span className="hidden sm:inline">{labels?.exportCSV}</span>
                  <span className="sm:hidden">CSV</span>
                </Button>
              </div>
              
              <Select
                options={languageOptions}
                value={language}
                onChange={setLanguage}
                className="w-full sm:w-32"
              />
            </div>
          </div>

          {/* Mobile-Responsive Dashboard Metrics */}
          <div className={`transition-all duration-300 overflow-hidden ${
            dashboardExpanded ? 'max-h-96 opacity-100 mb-4 sm:mb-6' : 'max-h-0 opacity-0 mb-0'
          }`}>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8 gap-2 sm:gap-4 p-3 sm:p-6 bg-card rounded-lg border border-border">
              <div className="text-center">
                <div className="text-lg sm:text-2xl font-bold text-primary">{totalGoals}</div>
                <div className="text-xs sm:text-sm text-muted-foreground">{labels?.totalGoals}</div>
              </div>
              <div className="text-center">
                <div className="text-lg sm:text-2xl font-bold text-yellow-600">{inProgressGoals}</div>
                <div className="text-xs sm:text-sm text-muted-foreground">{labels?.inProgress}</div>
              </div>
              <div className="text-center">
                <div className="text-lg sm:text-2xl font-bold text-green-600">{completedGoals}</div>
                <div className="text-xs sm:text-sm text-muted-foreground">{labels?.sectionCompleted}</div>
              </div>
              <div className="text-center">
                <div className="text-lg sm:text-2xl font-bold text-purple-600">{withMentorGoals}</div>
                <div className="text-xs sm:text-sm text-muted-foreground">{labels?.withMentor}</div>
              </div>
              <div className="text-center">
                <div className="text-lg sm:text-2xl font-bold text-blue-600">{plannedGoals}</div>
                <div className="text-xs sm:text-sm text-muted-foreground">{labels?.planned}</div>
              </div>
              <div className="text-center">
                <div className="text-lg sm:text-2xl font-bold text-orange-600">{followUpGoals}</div>
                <div className="text-xs sm:text-sm text-muted-foreground">{labels?.followUp}</div>
              </div>
              <div className="text-center">
                <div className="text-lg sm:text-2xl font-bold text-gray-600">{archivedGoals}</div>
                <div className="text-xs sm:text-sm text-muted-foreground">{language === 'en' ? 'Archived' : 'Arquivadas'}</div>
              </div>
            </div>
          </div>

          {/* Mobile-Responsive View Toggle Buttons */}
          <div className="flex items-center justify-center sm:justify-start mb-4 sm:mb-6">
            <div className="flex items-center space-x-1 sm:space-x-2 bg-card p-1 sm:p-2 rounded-lg border border-border w-fit">
              {viewButtons?.map(button => (
                <Button
                  key={button?.id}
                  variant={activeView === button?.id ? "default" : "ghost"}
                  iconName={button?.icon}
                  iconPosition="left"
                  iconSize={14}
                  onClick={() => setActiveView(button?.id)}
                  className={`transition-all duration-200 text-xs sm:text-sm h-8 sm:h-10 px-2 sm:px-4 ${
                    activeView === button?.id 
                      ? 'bg-primary text-primary-foreground shadow-sm' 
                      : 'hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  <span className="hidden sm:inline">{button?.label}</span>
                  <span className="sm:hidden">
                    {button?.id === 'grade' ? 'Grade' : 
                     button?.id === 'quadros' ? 'Board' : 'Time'}
                  </span>
                </Button>
              ))}
            </div>
          </div>

          {/* Mobile-Responsive Active View */}
          <div className="bg-card rounded-lg border border-border p-3 sm:p-6 min-h-[400px] sm:min-h-[600px] overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Carregando metas...</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <p className="text-red-600 mb-4">Erro: {error}</p>
                  <Button onClick={() => window.location.reload()}>
                    Tentar Novamente
                  </Button>
                </div>
              </div>
            ) : (
              renderActiveView()
            )}
          </div>
        </div>
      </main>
      
      {/* Goal Modal */}
      {showGoalModal && (
        <GoalModal
          isOpen={showGoalModal}
          onClose={handleCloseModal}
          onSave={editingGoal ? handleEditGoal : handleCreateGoal}
          goal={editingGoal || viewingGoal}
          isViewOnly={!!viewingGoal}
          language={language}
          initialStep={initialStep}
          loading={creating || updating}
        />
      )}
      <FloatingAIChat />
    </div>
  );
};

export default GoalsMapInterface;